import PropTypes from 'prop-types';
import { Grid, Divider, IconButton, Link, Button } from '@mui/material';
import NavigateBeforeIcon from '@mui/icons-material/NavigateBefore';
import NavigateNextIcon from '@mui/icons-material/NavigateNext';
import React, { useCallback, useEffect, useState, useMemo } from 'react';
import { ResponsivePaper } from '../DatePickerUI/responsivePaper';
import DatePickerMenu from '../DatePickerUI/datePickerMenu';
import StyledButton from './styledButton';
import { useDispatch, useSelector } from 'react-redux';
import { calendarActions } from '../../../modules/reducer/calendarSlice';
import { TransformDate } from '../DatePickerUtils/datePickerUtils';
import selectors from '../../../modules/selectors/datePickerSelectors';
import {
  AUDIENCE_ANALYSIS,
  CHANNEL_OPPS,
  OVERALL_PERFORMANCE,
  OVERALL_PERFORMANCE_SUB_BRANCH,
  PERFORMANCE_OVERVIEW_SUB_BRANCH,
  SUB_BRANCH_DASH_OPPS_BY_CHANNEL,
  TABLE_PERIOD_COMPARE,
  TABLE_YTD,
} from '../../../constants/datePickerConstants';
import { audienceAnalysisActions } from '../../../modules/reducer/audienceAnalysisSlice';
import { overallPerformanceActions } from '../../../modules/reducer/overallPerformanceSlice';
import { channelOppsActions } from '../../../modules/reducer/channelOppsSlice';
import { previousWeekExtractor } from '../../../data/branchPerformanceData';
import { subBranchDashOppsByChannelActions } from '../../../modules/reducer/subBranchDashOppsByChannelSlice';
import { performanceOverviewSubBranchActions } from '../../../modules/reducer/performanceOverviewSubBranch';
import { overallPerformanceSubBranchActions } from '../../../modules/reducer/overallPerformanceSubBranch';

export const DatePickerPopOver = ({
  component,
  onClose,
  weekSelection,
  type,
  tableType,
  compareWeekSelection,
  timePeriodPicker = false,
  comparisonPeriodPicker,
  restrictDataForPreviousYears = false,
}) => {
  const customFiltersForTimePeriod = restrictDataForPreviousYears
    ? ['LAST MONTH', 'LAST QUARTER', 'YTD']
    : ['LAST MONTH', 'LAST QUARTER', 'LAST YEAR', 'YTD'];
  const customFiltersForCompare = ['PREVIOUS PERIOD', 'PREVIOUS YEAR'];
  // Merge Q5 into Q4 if Q5 is present
  const updatedArr = weekSelection?.length > 0 && TransformDate.mergeQ5IntoQ4(weekSelection);
  const [selectedWeeks, setSelectedWeeks] = useState(updatedArr || weekSelection);
  const [selectedCompareWeeks, setSelectedCompareWeeks] = useState(compareWeekSelection);
  const [clickCount, setClickCount] = useState(0);
  const [arr, setArr] = useState([]);
  const timePeriod = timePeriodPicker;
  const comparisonPeriod = comparisonPeriodPicker;
  const [isResetEnabled, setIsResetEnabled] = useState(false);
  const [isApplyEnabled, setIsApplyEnabled] = useState(true);

  // COMPONENT STATES
  const { userData } = useSelector((state) => state.auth);
  const { audienceAnalysisRange, compareAudienceAnalysisRange } = useSelector(
    (state) => state.audienceAnalysis
  );
  const {
    overallPerformanceRange,
    overallPerformanceYTDRange,
    compareOverallPerformanceRangeTableOne,
    compareOverallPerformanceRangeYTD,
  } = useSelector((state) => state.overallPerformance);
  const {
    overallPerformanceSubBranchRange,
    overallPerformanceYTDSubBranchRange,
    compareOverallPerformanceSubBranchRangeTableOne,
    compareOverallPerformanceSubBranchRangeYTD,
  } = useSelector((state) => state.overallPerformanceSubBranch);

  const {
    transformedData,
    transformedDataWithDate,
    quarterData,
    quarterMenuList,
    yearMenuList,
    currQuarter,
    currYear,
    boundaryValue,
    quarterDataForCompare,
    quarterMenuListForCompare,
    yearMenuListForCompare,
    comparisonQuarter,
    comparisonYear,
    boundaryValueForCompare,
  } = useSelector(selectors.datePickerCombinedData);
  const { channelOppsRange } = useSelector((state) => state.channelOpps);
  const { subDashChannelOppsRange } = useSelector((state) => state.subBranchDashOppsByChannel);
  const { performanceOverviewSubBranchRange } = useSelector(
    (state) => state.performanceOverviewSubBranch
  );

  // Filter transformedData to show only currentYearData
  // Using useMemo to prevent infinite re-renders
  const filteredTransformedData = useMemo(() => {
    return restrictDataForPreviousYears
      ? transformedData?.filter((item) => item.currYear === userData?.data?.current_year)
      : transformedData;
  }, [component, transformedData]);

  const filteredTransformedDataWithDate = useMemo(() => {
    return restrictDataForPreviousYears
      ? transformedDataWithDate?.filter((item) => item.currYear === userData?.data?.current_year)
      : transformedDataWithDate;
  }, [component, transformedDataWithDate]);

  const isCurrYearSameAsSelYearTP =
    (restrictDataForPreviousYears
      ? filteredTransformedDataWithDate?.[0]?.currYear
      : transformedDataWithDate?.[0]?.currYear) === currYear;
  const isCurrYearSameAsSelYearCP =
    (restrictDataForPreviousYears
      ? filteredTransformedDataWithDate?.[0]?.currYear
      : transformedDataWithDate?.[0]?.currYear) === comparisonYear;
  const currentQuarterAccordingToDate = TransformDate?.currentQuarterExtractor(
    restrictDataForPreviousYears ? filteredTransformedDataWithDate : transformedDataWithDate
  );
  const updatedQuarterList = TransformDate?.filterQuarters(
    currentQuarterAccordingToDate,
    quarterMenuList
  );
  const updatedQuarterListCompare = TransformDate?.filterQuarters(
    currentQuarterAccordingToDate,
    quarterMenuListForCompare
  );
  const currentWeek = previousWeekExtractor(
    restrictDataForPreviousYears ? filteredTransformedDataWithDate : transformedDataWithDate
  )?.currentWeek; // current week has no data

  const getRange = () => {
    switch (component) {
      case AUDIENCE_ANALYSIS:
        return audienceAnalysisRange;
      case OVERALL_PERFORMANCE:
        return overallPerformanceRange;
      case OVERALL_PERFORMANCE_SUB_BRANCH:
        return overallPerformanceSubBranchRange;
      case CHANNEL_OPPS:
        return channelOppsRange;
      case SUB_BRANCH_DASH_OPPS_BY_CHANNEL:
        return subDashChannelOppsRange;
      case PERFORMANCE_OVERVIEW_SUB_BRANCH:
        return performanceOverviewSubBranchRange;
      default:
    }
  };

  const callDispatchForPreviousYear = (previousYear) => {
    if (previousYear) {
      switch (component) {
        case AUDIENCE_ANALYSIS:
          if (
            compareAudienceAnalysisRange &&
            Object.keys(compareAudienceAnalysisRange)?.length > 0
          ) {
            return dispatch(
              audienceAnalysisActions.setCompareAudienceAnalysisRange(
                TransformDate.comparisonPeriod.previousPeriod(compareAudienceAnalysisRange)
              )
            );
          }
          return dispatch(
            audienceAnalysisActions.setCompareAudienceAnalysisRange(
              TransformDate.comparisonPeriod.previousPeriod(previousYear)
            )
          );
        case OVERALL_PERFORMANCE:
          if (tableType === TABLE_PERIOD_COMPARE) {
            return dispatch(
              overallPerformanceActions.setCompareOverallPerformanceRangeForTableOne(
                TransformDate.comparisonPeriod.previousPeriod(
                  compareOverallPerformanceRangeTableOne
                )
              )
            );
          } else if (tableType === TABLE_YTD) {
            if (
              compareOverallPerformanceRangeYTD &&
              Object.keys(compareOverallPerformanceRangeYTD)?.length > 0
            ) {
              return dispatch(
                overallPerformanceActions.setCompareOverallPerformanceRangeYTD(
                  TransformDate.comparisonPeriod.previousPeriod(compareOverallPerformanceRangeYTD)
                )
              );
            }
            return dispatch(
              overallPerformanceActions.setCompareOverallPerformanceRangeYTD(
                TransformDate.comparisonPeriod.previousPeriod(overallPerformanceYTDRange)
              )
            );
          }
          break;
        case OVERALL_PERFORMANCE_SUB_BRANCH:
          if (tableType === TABLE_PERIOD_COMPARE) {
            return dispatch(
              overallPerformanceSubBranchActions.setCompareOverallPerformanceRangeForTableOne(
                TransformDate.comparisonPeriod.previousPeriod(
                  compareOverallPerformanceSubBranchRangeTableOne
                )
              )
            );
          } else if (tableType === TABLE_YTD) {
            if (
              compareOverallPerformanceSubBranchRangeYTD &&
              Object.keys(compareOverallPerformanceSubBranchRangeYTD)?.length > 0
            ) {
              return dispatch(
                overallPerformanceSubBranchActions.setCompareOverallPerformanceRangeYTD(
                  TransformDate.comparisonPeriod.previousPeriod(
                    compareOverallPerformanceSubBranchRangeYTD
                  )
                )
              );
            }
            return dispatch(
              overallPerformanceSubBranchActions.setCompareOverallPerformanceRangeYTD(
                TransformDate.comparisonPeriod.previousPeriod(overallPerformanceYTDSubBranchRange)
              )
            );
          }
          break;
        default:
      }
    }
  };

  const callDispatchForPreviousPeriod = (previousPeriod) => {
    if (previousPeriod) {
      switch (component) {
        case AUDIENCE_ANALYSIS:
          if (
            compareAudienceAnalysisRange &&
            Object.keys(compareAudienceAnalysisRange)?.length > 0
          ) {
            return dispatch(
              audienceAnalysisActions.setCompareAudienceAnalysisRange(
                TransformDate.comparisonPeriod.precedingPreviousPeriod(compareAudienceAnalysisRange)
              )
            );
          }
          return dispatch(
            audienceAnalysisActions.setCompareAudienceAnalysisRange(
              TransformDate.comparisonPeriod.precedingPreviousPeriod(previousPeriod)
            )
          );
        case OVERALL_PERFORMANCE:
          if (tableType === TABLE_PERIOD_COMPARE) {
            return dispatch(
              overallPerformanceActions.setCompareOverallPerformanceRangeForTableOne(
                TransformDate.comparisonPeriod.precedingPreviousPeriod(
                  compareOverallPerformanceRangeTableOne
                )
              )
            );
          }
          if (tableType === TABLE_YTD) {
            if (
              compareOverallPerformanceRangeYTD &&
              Object.keys(compareOverallPerformanceRangeYTD)?.length > 0
            ) {
              return dispatch(
                overallPerformanceActions.setCompareOverallPerformanceRangeYTD(
                  TransformDate.comparisonPeriod.precedingPreviousPeriod(
                    compareOverallPerformanceRangeYTD
                  )
                )
              );
            }
            return dispatch(
              overallPerformanceActions.setCompareOverallPerformanceRangeYTD(
                TransformDate.comparisonPeriod.precedingPreviousPeriod(overallPerformanceYTDRange)
              )
            );
          }
          break;
        case OVERALL_PERFORMANCE_SUB_BRANCH:
          if (tableType === TABLE_PERIOD_COMPARE) {
            return dispatch(
              overallPerformanceSubBranchActions.setCompareOverallPerformanceRangeForTableOne(
                TransformDate.comparisonPeriod.precedingPreviousPeriod(
                  compareOverallPerformanceSubBranchRangeTableOne
                )
              )
            );
          }
          if (tableType === TABLE_YTD) {
            if (
              compareOverallPerformanceSubBranchRangeYTD &&
              Object.keys(compareOverallPerformanceSubBranchRangeYTD)?.length > 0
            ) {
              return dispatch(
                overallPerformanceSubBranchActions.setCompareOverallPerformanceRangeYTD(
                  TransformDate.comparisonPeriod.precedingPreviousPeriod(
                    compareOverallPerformanceSubBranchRangeYTD
                  )
                )
              );
            }
            return dispatch(
              overallPerformanceSubBranchActions.setCompareOverallPerformanceRangeYTD(
                TransformDate.comparisonPeriod.precedingPreviousPeriod(
                  overallPerformanceYTDSubBranchRange
                )
              )
            );
          }
          break;
        default:
      }
    }
  };

  const callDispatchForYTD = (ytd) => {
    switch (component) {
      case AUDIENCE_ANALYSIS:
        return dispatch(audienceAnalysisActions.setAudienceAnalysisRange(ytd));
      case CHANNEL_OPPS:
        return dispatch(channelOppsActions.setChannelOppsRange(ytd));
      case SUB_BRANCH_DASH_OPPS_BY_CHANNEL:
        return dispatch(subBranchDashOppsByChannelActions.setSubDashChannelOppsRange(ytd));
      case OVERALL_PERFORMANCE:
        return dispatch(overallPerformanceActions.setOverallPerformanceRange(ytd));
      case OVERALL_PERFORMANCE_SUB_BRANCH:
        return dispatch(overallPerformanceSubBranchActions.setOverallPerformanceRange(ytd));
      case PERFORMANCE_OVERVIEW_SUB_BRANCH:
        return dispatch(
          performanceOverviewSubBranchActions.setPerformanceOverviewSubBranchRange(ytd)
        );
      default:
    }
  };

  const callDispatchForLastMonth = (lastMonthRange) => {
    switch (component) {
      case AUDIENCE_ANALYSIS:
        return dispatch(audienceAnalysisActions.setAudienceAnalysisRange(lastMonthRange));
      case CHANNEL_OPPS:
        return dispatch(channelOppsActions.setChannelOppsRange(lastMonthRange));
      case SUB_BRANCH_DASH_OPPS_BY_CHANNEL:
        return dispatch(
          subBranchDashOppsByChannelActions.setSubDashChannelOppsRange(lastMonthRange)
        );
      case PERFORMANCE_OVERVIEW_SUB_BRANCH:
        return dispatch(
          performanceOverviewSubBranchActions.setPerformanceOverviewSubBranchRange(lastMonthRange)
        );
      case OVERALL_PERFORMANCE:
        return dispatch(overallPerformanceActions.setOverallPerformanceRange(lastMonthRange));
      case OVERALL_PERFORMANCE_SUB_BRANCH:
        return dispatch(
          overallPerformanceSubBranchActions.setOverallPerformanceRange(lastMonthRange)
        );
      default:
    }
  };

  const callDispatchForLastYear = (lastYear) => {
    if (lastYear) {
      switch (component) {
        case AUDIENCE_ANALYSIS:
          return dispatch(audienceAnalysisActions.setAudienceAnalysisRange(lastYear));
        case CHANNEL_OPPS:
          return dispatch(channelOppsActions.setChannelOppsRange(lastYear));
        case SUB_BRANCH_DASH_OPPS_BY_CHANNEL:
          return dispatch(subBranchDashOppsByChannelActions.setSubDashChannelOppsRange(lastYear));
        case PERFORMANCE_OVERVIEW_SUB_BRANCH:
          return dispatch(
            performanceOverviewSubBranchActions.setPerformanceOverviewSubBranchRange(lastYear)
          );
        case OVERALL_PERFORMANCE:
          return dispatch(overallPerformanceActions.setOverallPerformanceRange(lastYear));
        case OVERALL_PERFORMANCE_SUB_BRANCH:
          return dispatch(overallPerformanceSubBranchActions.setOverallPerformanceRange(lastYear));
        default:
      }
    }
  };

  const callDispatchForLastQuarter = (lastQuarter) => {
    if (lastQuarter) {
      switch (component) {
        case AUDIENCE_ANALYSIS:
          return dispatch(audienceAnalysisActions.setAudienceAnalysisRange(lastQuarter));
        case CHANNEL_OPPS:
          return dispatch(channelOppsActions.setChannelOppsRange(lastQuarter));
        case SUB_BRANCH_DASH_OPPS_BY_CHANNEL:
          return dispatch(
            subBranchDashOppsByChannelActions.setSubDashChannelOppsRange(lastQuarter)
          );
        case PERFORMANCE_OVERVIEW_SUB_BRANCH:
          return dispatch(
            performanceOverviewSubBranchActions.setPerformanceOverviewSubBranchRange(lastQuarter)
          );
        case OVERALL_PERFORMANCE:
          return dispatch(overallPerformanceActions.setOverallPerformanceRange(lastQuarter));
        case OVERALL_PERFORMANCE_SUB_BRANCH:
          return dispatch(
            overallPerformanceSubBranchActions.setOverallPerformanceRange(lastQuarter)
          );
        default:
      }
    }
  };

  const dispatch = useDispatch();

  const quarterListHandler = useCallback(() => {
    const dataToUse = restrictDataForPreviousYears ? filteredTransformedData : transformedData;
    if (dataToUse.length > 0 && currYear) {
      dispatch(
        calendarActions.getQuarterList({
          quarter: currQuarter ?? 'Q4',
          year: currYear,
          transformedData: dataToUse,
        })
      );
    }
  }, [dispatch, currQuarter, currYear, component, filteredTransformedData, transformedData]);

  const quarterListHandlerForCompare = useCallback(() => {
    const dataToUse = restrictDataForPreviousYears ? filteredTransformedData : transformedData;
    if (dataToUse.length > 0) {
      dispatch(
        calendarActions.getQuarterListForCompare({
          quarter: comparisonQuarter || 'Q4',
          year: comparisonYear,
          transformedData: dataToUse,
        })
      );
    }
  }, [
    dispatch,
    comparisonQuarter,
    comparisonYear,
    component,
    filteredTransformedData,
    transformedData,
  ]);

  const menuListHandler = useCallback(() => {
    // here for the boundry
    const dataToUse = restrictDataForPreviousYears ? filteredTransformedData : transformedData;
    const dataWithDateToUse = restrictDataForPreviousYears
      ? filteredTransformedDataWithDate
      : transformedDataWithDate;
    if (dataToUse.length > 0) {
      dispatch(calendarActions.updateQuarterYearMenuList(dataToUse));
      const dispatchObject = {
        transformData: dataToUse,
        transformDataWithDate: dataWithDateToUse,
        current_year: userData?.data?.current_year,
      };
      dispatch(calendarActions.updateBoundaryValues(dispatchObject));
    }
  }, [
    dispatch,
    component,
    filteredTransformedData,
    filteredTransformedDataWithDate,
    transformedData,
    transformedDataWithDate,
    userData,
  ]);

  const menuListHandlerForCompare = useCallback(() => {
    const dataToUse = restrictDataForPreviousYears ? filteredTransformedData : transformedData;
    const dataWithDateToUse = restrictDataForPreviousYears
      ? filteredTransformedDataWithDate
      : transformedDataWithDate;
    if (dataToUse.length > 0) {
      dispatch(calendarActions.updateQuarterYearMenuListForCompare(dataToUse));
      const dispatchObject = {
        transformData: dataToUse,
        transformDataWithDate: dataWithDateToUse,
        current_year: userData?.data?.current_year,
      };

      dispatch(calendarActions.updateBoundaryValuesForCompare(dispatchObject));
    }
  }, [
    dispatch,
    component,
    filteredTransformedData,
    filteredTransformedDataWithDate,
    transformedData,
    transformedDataWithDate,
    userData,
  ]);

  const handleResetSelection = () => {
    if (timePeriod) {
      setSelectedWeeks([]);
    } else {
      setSelectedCompareWeeks([]);
    }
    setClickCount(0);
    setArr([]);
    setIsApplyEnabled(true);
  };

  useEffect(() => {
    quarterListHandler();
    menuListHandler();
    quarterListHandlerForCompare();
    menuListHandlerForCompare();
  }, [
    quarterListHandler,
    menuListHandler,
    quarterListHandlerForCompare,
    menuListHandlerForCompare,
    dispatch,
  ]);

  const handleMoveBackward = () => {
    if (Object.keys(boundaryValue).length > 0) {
      dispatch(
        calendarActions.moveBackward({
          quarter: currQuarter ?? 'Q4',
          year: currYear,
          startingYear: boundaryValue.startingYear,
        })
      );
    }
  };

  const handleMoveForward = () => {
    if (Object.keys(boundaryValue).length > 0) {
      dispatch(
        calendarActions.moveForward({
          quarter: currQuarter ?? 'Q4',
          year: currYear,
          endingYear: boundaryValue.endingYear,
          endingQuarter: boundaryValue.endingQuarter,
        })
      );
    }
  };

  const handleMoveBackwardForCompare = () => {
    if (Object.keys(boundaryValueForCompare).length > 0) {
      dispatch(
        calendarActions.moveBackwardForCompare({
          quarter: comparisonQuarter,
          year: comparisonYear,
          startingYear: boundaryValueForCompare.startingYear,
        })
      );
    }
  };

  const handleMoveForwardForCompare = () => {
    if (Object.keys(boundaryValueForCompare).length > 0) {
      dispatch(
        calendarActions.moveForwardForCompare({
          quarter: comparisonQuarter,
          year: comparisonYear,
          endingYear: boundaryValueForCompare.endingYear,
          endingQuarter: boundaryValueForCompare.endingQuarter,
        })
      );
    }
  };

  const quarterEntries = quarterData && Object.entries(quarterData);
  const firstTwoMonths = quarterEntries && Object.fromEntries(quarterEntries?.slice(0, 2));
  const lastMonth = quarterEntries && Object.fromEntries(quarterEntries?.slice(2));

  const quarterEntriesForCompare = useMemo(() => {
    if (!comparisonPeriod || !quarterDataForCompare) return null;

    // Filter out entries with valid `va.id` values
    const filteredEntries = Object.entries(quarterDataForCompare).filter(
      ([_key, value]) => value?.va?.id !== null && value?.va?.id !== undefined
    );

    // If no valid entries are found, return the initial values
    if (filteredEntries.length === 0) {
      return Object.entries(quarterDataForCompare);
    }

    return filteredEntries;
  }, [comparisonPeriod, quarterDataForCompare]);

  const firstTwoMonthsForCompareRef = React.useRef(null);
  const lastMonthForCompareRef = React.useRef(null);

  const firstTwoMonthsForCompare = useMemo(() => {
    if (!comparisonPeriod || !quarterEntriesForCompare) {
      return firstTwoMonthsForCompareRef.current;
    }
    const result = Object.fromEntries(quarterEntriesForCompare.slice(0, 2));
    firstTwoMonthsForCompareRef.current = result;
    return result;
  }, [comparisonPeriod, quarterEntriesForCompare]);

  const lastMonthForCompare = useMemo(() => {
    if (!comparisonPeriod || !quarterEntriesForCompare) {
      return lastMonthForCompareRef.current;
    }
    const result = Object.fromEntries(quarterEntriesForCompare.slice(2));
    lastMonthForCompareRef.current = result;
    return result;
  }, [comparisonPeriod, quarterEntriesForCompare]);

  const handleWeekClick = (month, week, quarter, year) => {
    let updatedArr = [...arr]; // Create a copy of the array
    const yearIndex = updatedArr.findIndex((item) => item.currYear === year);

    if (yearIndex !== -1) {
      const existingYear = updatedArr[yearIndex];
      if (!existingYear[quarter]) {
        existingYear[quarter] = {};
      }
      if (existingYear[quarter][month] && !existingYear[quarter][month].includes(week)) {
        existingYear[quarter][month].push(week);
      } else {
        existingYear[quarter][month] = [week];
      }
    } else {
      updatedArr.push({
        currYear: year,
        [quarter]: { [month]: [week] },
      });
    }

    // Increment the click count
    setClickCount((prevCount) => prevCount + 1);

    let weekRange;
    // Update arr state and reset click count if necessary
    if (clickCount > 1) {
      updatedArr = [];
      setArr([]);
      updatedArr.push({
        currYear: year,
        [quarter]: { [month]: [week] },
      });
      setArr(updatedArr);
      setClickCount(0);
    } else {
      updatedArr[updatedArr.length - 1][quarter][month] =
        updatedArr[updatedArr.length - 1][quarter][month].sort();
      setArr(updatedArr);
    }

    // Merge Q5 into Q4 if Q5 is present
    updatedArr = TransformDate.mergeQ5IntoQ4(updatedArr);

    updatedArr = TransformDate.sortByYear(updatedArr);
    updatedArr[updatedArr.length - 1] = TransformDate.sortOnKeys(updatedArr[updatedArr.length - 1]);
    updatedArr[updatedArr.length - 1][quarter] = TransformDate.getSortedMonthData(
      updatedArr[updatedArr.length - 1][quarter]
    );

    weekRange = TransformDate.getSelectedRange(transformedData, updatedArr);
    timePeriod && setSelectedWeeks(weekRange);
    comparisonPeriod && setSelectedCompareWeeks(weekRange);

    if (clickCount === 1) {
      setIsApplyEnabled(false);
      setClickCount(0);
      setArr([]);
    } else {
      setIsApplyEnabled(true);
      setIsResetEnabled(false);
    }
  };

  const clearStateHandler = () => {
    setSelectedWeeks([]);
    setSelectedCompareWeeks([]);
  };

  const checkForGlobalObject = (object) => {
    if (object === null || object === undefined) {
      return false; // Return false if object is null or undefined
    }
    return (
      Object.values(object).every((value) => value === null || value === undefined) ||
      Object.values(object).some((value) => value === null || value === undefined)
    );
  };

  const handleQuickFilters = (e) => {
    const obj =
      transformedDataWithDate && TransformDate.getCurrentYearAndQuarter(transformedDataWithDate);
    const allValuesAreNull = checkForGlobalObject(getRange());
    if (obj && Object.keys(obj).length) {
      switch (e.currentTarget.value) {
        case 'LAST WEEK': {
          break;
        }
        case 'LAST MONTH': {
          const lastMonthRange = TransformDate.quickFilters.LAST_MONTH(
            transformedData,
            obj.year,
            transformedDataWithDate
          );
          callDispatchForLastMonth(lastMonthRange);
          clearStateHandler();
          break;
        }
        case 'LAST QUARTER': {
          // Adding currYear to handle if current quarter is Q1, the last quarter will be Q4 and 1 year behind
          const currentYear = obj.quarter === 'Q1' ? obj.year - 1 : obj.year;
          // made changes because last quarter was always set to default Q4
          const previousQuarter = Number(obj?.quarter.slice(1)) - 1;
          const lastQuarter = TransformDate.quickFilters.LAST_QUARTER(
            transformedData,
            currentYear,
            transformedDataWithDate,
            previousQuarter
          );
          callDispatchForLastQuarter(lastQuarter);
          clearStateHandler();
          break;
        }
        case 'LAST YEAR': {
          const lastYear = TransformDate.quickFilters.LAST_YEAR(
            transformedDataWithDate,
            userData?.data?.current_year
          );
          callDispatchForLastYear(lastYear);
          clearStateHandler();
          break;
        }
        case 'YTD': {
          const ytd = TransformDate.quickFilters.YEAR_TO_DATE(
            transformedDataWithDate,
            previousWeekExtractor
          );
          callDispatchForYTD(ytd);
          clearStateHandler();
          break;
        }
        case 'PREVIOUS PERIOD': {
          const previousPeriod =
            !allValuesAreNull && TransformDate.comparisonPeriod.previousPeriod(getRange());
          callDispatchForPreviousPeriod(previousPeriod);
          break;
        }
        case 'PREVIOUS YEAR': {
          const previousYear =
            !allValuesAreNull && TransformDate.comparisonPeriod.previousPeriod(getRange());
          callDispatchForPreviousYear(previousYear);
          break;
        }
        default:
      }
    }
    onClose();
  };

  const handleCloseForPopOver = () => {
    const _selectedWeeks = TransformDate.sortByYear(selectedWeeks);

    if (
      (_selectedWeeks !== undefined && _selectedWeeks.length) ||
      (selectedCompareWeeks !== undefined && selectedCompareWeeks.length)
    ) {
      // Extract the nested ternary into a more readable format
      let customRange;
      if (_selectedWeeks) {
        customRange = TransformDate.quickFilters.CUSTOM(_selectedWeeks);
      } else if (selectedCompareWeeks) {
        customRange = TransformDate.quickFilters.CUSTOM(selectedCompareWeeks);
      } else {
        customRange = undefined;
      }
      switch (component) {
        case OVERALL_PERFORMANCE:
          if (type === 'compare') {
            if (tableType === TABLE_PERIOD_COMPARE) {
              dispatch(
                overallPerformanceActions.setCompareOverallPerformanceRangeForTableOne(customRange)
              );
              const arrOfKeys = TransformDate.getKeysFromObj(selectedCompareWeeks);
              arrOfKeys.length
                ? dispatch(overallPerformanceActions.setCompareSelectedQuarter(arrOfKeys[0]))
                : dispatch(overallPerformanceActions.setCompareSelectedQuarter(comparisonQuarter));
              arrOfKeys.length
                ? dispatch(overallPerformanceActions.setCompareSelectedYear(arrOfKeys[1]))
                : dispatch(overallPerformanceActions.setCompareSelectedYear(comparisonYear));
              onClose();
            }
            if (tableType === TABLE_YTD) {
              dispatch(overallPerformanceActions.setCompareOverallPerformanceRangeYTD(customRange));
              const arrOfKeys = TransformDate.getKeysFromObj(selectedCompareWeeks);
              arrOfKeys.length
                ? dispatch(overallPerformanceActions.setCompareSelectedQuarter(arrOfKeys[0]))
                : dispatch(overallPerformanceActions.setCompareSelectedQuarter(comparisonQuarter));
              arrOfKeys.length
                ? dispatch(overallPerformanceActions.setCompareSelectedYear(arrOfKeys[1]))
                : dispatch(overallPerformanceActions.setCompareSelectedYear(comparisonYear));
              onClose();
            }
          } else {
            dispatch(overallPerformanceActions.setOverallPerformanceRange(customRange));
            const arrOfKeys = TransformDate.getKeysFromObj(_selectedWeeks);
            arrOfKeys.length
              ? dispatch(overallPerformanceActions.setCurrentQuarter(arrOfKeys[0]))
              : dispatch(overallPerformanceActions.setCurrentQuarter(currQuarter));
            arrOfKeys.length
              ? dispatch(overallPerformanceActions.setCurrentYear(arrOfKeys[1]))
              : dispatch(overallPerformanceActions.setCurrentYear(currYear));
            onClose();
          }
          break;
        case OVERALL_PERFORMANCE_SUB_BRANCH:
          if (type === 'compare') {
            if (tableType === TABLE_PERIOD_COMPARE) {
              dispatch(
                overallPerformanceSubBranchActions.setCompareOverallPerformanceRangeForTableOne(
                  customRange
                )
              );
              const arrOfKeys = TransformDate.getKeysFromObj(selectedCompareWeeks);
              arrOfKeys.length
                ? dispatch(
                    overallPerformanceSubBranchActions.setCompareSelectedQuarter(arrOfKeys[0])
                  )
                : dispatch(
                    overallPerformanceSubBranchActions.setCompareSelectedQuarter(comparisonQuarter)
                  );
              arrOfKeys.length
                ? dispatch(overallPerformanceSubBranchActions.setCompareSelectedYear(arrOfKeys[1]))
                : dispatch(
                    overallPerformanceSubBranchActions.setCompareSelectedYear(comparisonYear)
                  );
              onClose();
            }
            if (tableType === TABLE_YTD) {
              dispatch(
                overallPerformanceSubBranchActions.setCompareOverallPerformanceRangeYTD(customRange)
              );
              const arrOfKeys = TransformDate.getKeysFromObj(selectedCompareWeeks);
              arrOfKeys.length
                ? dispatch(
                    overallPerformanceSubBranchActions.setCompareSelectedQuarter(arrOfKeys[0])
                  )
                : dispatch(
                    overallPerformanceSubBranchActions.setCompareSelectedQuarter(comparisonQuarter)
                  );
              arrOfKeys.length
                ? dispatch(overallPerformanceSubBranchActions.setCompareSelectedYear(arrOfKeys[1]))
                : dispatch(
                    overallPerformanceSubBranchActions.setCompareSelectedYear(comparisonYear)
                  );
              onClose();
            }
          } else {
            dispatch(overallPerformanceSubBranchActions.setOverallPerformanceRange(customRange));
            const arrOfKeys = TransformDate.getKeysFromObj(_selectedWeeks);
            arrOfKeys.length
              ? dispatch(overallPerformanceSubBranchActions.setCurrentQuarter(arrOfKeys[0]))
              : dispatch(overallPerformanceSubBranchActions.setCurrentQuarter(currQuarter));
            arrOfKeys.length
              ? dispatch(overallPerformanceSubBranchActions.setCurrentYear(arrOfKeys[1]))
              : dispatch(overallPerformanceSubBranchActions.setCurrentYear(currYear));
            onClose();
          }
          break;
        case AUDIENCE_ANALYSIS:
          // Custom Range Dispatch for Audience Analysis
          if (type === 'compare') {
            dispatch(audienceAnalysisActions.setCompareAudienceAnalysisRange(customRange));
            const arrOfKeys = TransformDate.getKeysFromObj(selectedCompareWeeks);
            arrOfKeys.length
              ? dispatch(audienceAnalysisActions.setCompareSelectedQuarter(arrOfKeys[0]))
              : dispatch(audienceAnalysisActions.setCompareSelectedQuarter(comparisonQuarter));
            arrOfKeys.length
              ? dispatch(audienceAnalysisActions.setCompareSelectedYear(arrOfKeys[1]))
              : dispatch(audienceAnalysisActions.setCompareSelectedYear(comparisonYear));
            onClose();
          } else {
            dispatch(audienceAnalysisActions.setAudienceAnalysisRange(customRange));
            const arrOfKeys = TransformDate.getKeysFromObj(_selectedWeeks);
            arrOfKeys.length
              ? dispatch(audienceAnalysisActions.setCurrentQuarter(arrOfKeys[0]))
              : dispatch(audienceAnalysisActions.setCurrentQuarter(currQuarter));
            arrOfKeys.length
              ? dispatch(audienceAnalysisActions.setCurrentYear(arrOfKeys[1]))
              : dispatch(audienceAnalysisActions.setCurrentYear(currYear));
            onClose();
          }
          break;
        case CHANNEL_OPPS: {
          // Custom Range Dispatch for Channel Opps
          dispatch(channelOppsActions.setChannelOppsRange(customRange));
          const arrayOfKeys = TransformDate.getKeysFromObj(_selectedWeeks);
          arrayOfKeys.length
            ? dispatch(channelOppsActions.setCurrentQuarter(arrayOfKeys[0]))
            : dispatch(channelOppsActions.setCurrentQuarter(currQuarter));
          arrayOfKeys.length
            ? dispatch(channelOppsActions.setCurrentYear(arrayOfKeys[1]))
            : dispatch(channelOppsActions.setCurrentYear(currYear));
          onClose();
          break;
        }
        case SUB_BRANCH_DASH_OPPS_BY_CHANNEL: {
          // Custom Range Dispatch for Sub Branch Channel Opps
          dispatch(subBranchDashOppsByChannelActions.setSubDashChannelOppsRange(customRange));
          const arrayOfKeysSubBranch = TransformDate.getKeysFromObj(_selectedWeeks);
          arrayOfKeysSubBranch.length
            ? dispatch(subBranchDashOppsByChannelActions.setCurrentQuarter(arrayOfKeysSubBranch[0]))
            : dispatch(subBranchDashOppsByChannelActions.setCurrentQuarter(currQuarter));
          arrayOfKeysSubBranch.length
            ? dispatch(subBranchDashOppsByChannelActions.setCurrentYear(arrayOfKeysSubBranch[1]))
            : dispatch(subBranchDashOppsByChannelActions.setCurrentYear(currYear));
          onClose();
          break;
        }
        case PERFORMANCE_OVERVIEW_SUB_BRANCH: {
          // Custom Range Dispatch for performance Overview Sub Branch
          dispatch(
            performanceOverviewSubBranchActions.setPerformanceOverviewSubBranchRange(customRange)
          );
          const arrayOfKeysSubBranch = TransformDate.getKeysFromObj(_selectedWeeks);
          arrayOfKeysSubBranch.length
            ? dispatch(
                performanceOverviewSubBranchActions.setCurrentQuarter(arrayOfKeysSubBranch[0])
              )
            : dispatch(performanceOverviewSubBranchActions.setCurrentQuarter(currQuarter));
          arrayOfKeysSubBranch.length
            ? dispatch(performanceOverviewSubBranchActions.setCurrentYear(arrayOfKeysSubBranch[1]))
            : dispatch(performanceOverviewSubBranchActions.setCurrentYear(currYear));
          onClose();
          break;
        }
        default:
      }
    }
  };

  return (
    <ResponsivePaper>
      <Grid container spacing={2} m={0}>
        <Grid item xs={8}>
          <Grid container>
            <Grid item xs={2}>
              {timePeriod && (
                <IconButton
                  aria-label="backward nav"
                  disabled={Object.keys(boundaryValue).length === 0}
                  color="secondary"
                  onClick={handleMoveBackward}
                >
                  <NavigateBeforeIcon />
                </IconButton>
              )}
              {comparisonPeriod && (
                <IconButton
                  aria-label="backward nav"
                  disabled={Object.keys(boundaryValueForCompare).length === 0}
                  color="secondary"
                  onClick={handleMoveBackwardForCompare}
                >
                  <NavigateBeforeIcon />
                </IconButton>
              )}
            </Grid>
            <Grid item container xs={8} justifyContent="space-evenly" alignContent="center">
              <Grid item xs={4}>
                {timePeriod && (
                  <DatePickerMenu
                    timePeriod={timePeriod}
                    data={isCurrYearSameAsSelYearTP ? updatedQuarterList : quarterMenuList}
                    defaultOption={currQuarter ?? 'Q4'}
                    type="quarter"
                  />
                )}
                {comparisonPeriod && (
                  <DatePickerMenu
                    comparisonPeriod={comparisonPeriod}
                    data={
                      isCurrYearSameAsSelYearCP
                        ? updatedQuarterListCompare
                        : quarterMenuListForCompare
                    }
                    defaultOption={comparisonQuarter}
                    type="quarter"
                  />
                )}
              </Grid>
              <Grid item xs={4}>
                {timePeriod && (
                  <DatePickerMenu
                    timePeriod={timePeriod}
                    data={
                      restrictDataForPreviousYears
                        ? yearMenuList?.filter((year) => year === userData?.data?.current_year)
                        : yearMenuList
                    }
                    defaultOption={currYear}
                    type="year"
                  />
                )}
                {comparisonPeriod && (
                  <DatePickerMenu
                    comparisonPeriod={comparisonPeriod}
                    data={
                      restrictDataForPreviousYears
                        ? yearMenuListForCompare?.filter(
                            (year) => year === userData?.data?.current_year
                          )
                        : yearMenuListForCompare
                    }
                    defaultOption={comparisonYear}
                    type="year"
                  />
                )}
              </Grid>
            </Grid>
            <Grid item xs={2}>
              {timePeriod && (
                <IconButton
                  aria-label="forward nav"
                  disabled={Object.keys(boundaryValue).length === 0}
                  color="secondary"
                  onClick={handleMoveForward}
                >
                  <NavigateNextIcon />
                </IconButton>
              )}
              {comparisonPeriod && (
                <IconButton
                  aria-label="forward nav"
                  disabled={Object.keys(boundaryValueForCompare).length === 0}
                  color="secondary"
                  onClick={handleMoveForwardForCompare}
                >
                  <NavigateNextIcon />
                </IconButton>
              )}
            </Grid>
          </Grid>
          <Grid container spacing={2} mt={1}>
            {timePeriod &&
              Object.entries(firstTwoMonths)?.map(([month, weeks]) => (
                <StyledButton
                  key={month}
                  month={month}
                  weeks={weeks}
                  quarter={currQuarter ?? 'Q4'}
                  year={currYear}
                  selectedWeeks={selectedWeeks}
                  onWeekClick={handleWeekClick}
                  timePeriod={timePeriod}
                  isCurrYearSame={isCurrYearSameAsSelYearTP}
                  currentWeek={currentWeek}
                />
              ))}
            {comparisonPeriod &&
              Object.entries(firstTwoMonthsForCompare)?.map(([month, weeks]) => (
                <StyledButton
                  key={month}
                  month={month}
                  weeks={weeks}
                  quarter={comparisonQuarter ?? 'Q4'}
                  year={comparisonYear}
                  selectedWeeks={selectedCompareWeeks}
                  onWeekClick={handleWeekClick}
                  comparisonPeriod={comparisonPeriod}
                  isCurrYearSame={isCurrYearSameAsSelYearCP}
                  currentWeek={currentWeek}
                />
              ))}
          </Grid>
          <Grid container spacing={2} p={3}>
            <Grid item xs={12}>
              {timePeriod && Object.keys(lastMonth).length > 0 && (
                <Grid item container xs={12} justifyContent="space-evenly" alignContent="center">
                  {Object.entries(lastMonth).map(([month, weeks]) => (
                    <StyledButton
                      key={month}
                      month={month}
                      weeks={weeks}
                      quarter={currQuarter ?? 'Q4'}
                      year={currYear}
                      selectedWeeks={selectedWeeks}
                      onWeekClick={handleWeekClick}
                      timePeriod={timePeriod}
                      isCurrYearSame={isCurrYearSameAsSelYearTP}
                      currentWeek={currentWeek}
                    />
                  ))}
                </Grid>
              )}
              {comparisonPeriod && Object.keys(lastMonthForCompare).length > 0 && (
                <Grid item container xs={12} justifyContent="space-evenly" alignContent="center">
                  {Object.entries(lastMonthForCompare).map(([month, weeks]) => (
                    <StyledButton
                      key={month}
                      month={month}
                      weeks={weeks}
                      quarter={comparisonQuarter ?? 'Q4'}
                      year={comparisonYear}
                      selectedWeeks={selectedCompareWeeks}
                      onWeekClick={handleWeekClick}
                      comparisonPeriod={comparisonPeriod}
                      isCurrYearSame={isCurrYearSameAsSelYearCP}
                      currentWeek={currentWeek}
                    />
                  ))}
                </Grid>
              )}
            </Grid>
          </Grid>
          <Grid container spacing={2} p={3}>
            <Grid
              item
              container
              xs={12}
              justifyContent="center"
              alignContent="center"
              sx={{ ml: '-6.6px' }}
            >
              <Button
                variant="outlined"
                sx={{ mr: '7px' }}
                color="secondary"
                onClick={handleResetSelection}
                disabled={isResetEnabled}
              >
                Reset
              </Button>
              <Button
                variant="contained"
                sx={{ mr: '7px' }}
                onClick={handleCloseForPopOver}
                disabled={isApplyEnabled}
              >
                Apply
              </Button>
            </Grid>
          </Grid>
        </Grid>
        <Divider orientation="vertical" flexItem></Divider>
        {type === 'compare' ? (
          <Grid
            item
            container
            xs={3}
            direction="column"
            justifyContent="center"
            alignContent="flex-start"
          >
            {customFiltersForCompare.map((filter) => {
              return (
                <Link
                  key={filter}
                  style={{ textAlign: 'left' }}
                  underline="hover"
                  component="button"
                  variant="body2"
                  color="secondary"
                  sx={{ marginBottom: '2rem' }}
                  onClick={handleQuickFilters}
                  value={filter}
                >
                  {filter}
                </Link>
              );
            })}
          </Grid>
        ) : (
          <Grid
            item
            container
            xs={3}
            direction="column"
            justifyContent="space-evenly"
            alignContent="flex-start"
          >
            {customFiltersForTimePeriod.map((filter) => {
              return (
                <Link
                  key={filter}
                  style={{ textAlign: 'left' }}
                  underline="hover"
                  component="button"
                  variant="body2"
                  color="secondary"
                  onClick={handleQuickFilters}
                  value={filter}
                >
                  {filter}
                </Link>
              );
            })}
          </Grid>
        )}
      </Grid>
    </ResponsivePaper>
  );
};

// Prop validation
DatePickerPopOver.propTypes = {
  component: PropTypes.element,
  onClose: PropTypes.func,
  weekSelection: PropTypes.any,
  type: PropTypes.string,
  tableType: PropTypes.string,
  compareWeekSelection: PropTypes.array,
  timePeriodPicker: PropTypes.bool,
  comparisonPeriodPicker: PropTypes.bool,
  restrictDataForPreviousYears: PropTypes.bool,
};
